import CircleAnimation from "@assets/lotties/LandingDeltaCircle.json"
import LinesAnimation from "@assets/lotties/LandingDeltaLines.json"
import LogoAnimation from "@assets/lotties/LandingEchoLogo.json"
import { t, Trans } from "@lingui/macro"
import { gsap } from "gsap"
import { useEffect, useMemo, useRef, useState } from "react"
import Lottie, { Options as LottieOptions } from "react-lottie"

import { Wrapper, Background, LandingWrapper } from "../delta-landing/delta-landing.styles"
import { AnimatedTextWrapper, AnimatedText } from "./echo-landing.styles"

const linesOptions: LottieOptions = {
	loop: false,
	autoplay: true,
	animationData: LinesAnimation,
	rendererSettings: {
		preserveAspectRatio: "none meet", // More info about the setting https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/preserveAspectRatio
	},
}
const circleOptions: LottieOptions = {
	loop: false,
	autoplay: false,
	animationData: CircleAnimation,
	rendererSettings: {
		preserveAspectRatio: "none slice",
	},
}

const lottieStyle: React.CSSProperties = {
	position: "absolute",
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
}

export interface IEchoLandingProps {
	onNext: () => void
	onboardingType: string | null
}

export const EchoLanding = (props: IEchoLandingProps) => {
	const [isStoppedCircleAnimation, setIsStoppedCircleAnimation] = useState<boolean>(true)

	const helloAnimatedRef = useRef(null)
	const weAnimatedRef = useRef(null)
	const careAnimatedRef = useRef(null)
	const chosenAnimatedRef = useRef(null)
	const genderAnimatedRef = useRef(null)

	const timeline = [
		{
			text: t`Hello!`,
			start: 0,
			end: 1.5,
			position: "top",
		},
		{
			text: t`We are Pora!`,
			start: 1.6,
			end: 3,
			position: "bottom",
		},
		{
			text: t`Take care of your skin, hair, body and mind`,
			start: 3.1,
			end: 6.5,
			position: "top",
		},
		{
			text: t`Chosen by`,
			start: 7,
			end: 12,
			position: "top",
		},
		{
			text: !props.onboardingType ? t`Women` : t`Men`,
			start: 7.8,
			end: 12,
			position: "bottom",
		},
	]

	const logoOptions = useMemo(
		() => ({
			loop: false,
			autoplay: true,
			animationData: LogoAnimation,
			rendererSettings: {
				preserveAspectRatio: "xMidyMid meet",
				clearCanvas: true,
				hideOnTransparent: true,
			},
		}),
		[]
	)

	useEffect(() => {
		if (helloAnimatedRef && helloAnimatedRef.current) {
			gsap.from(helloAnimatedRef.current, {
				y: 100,
			})
			gsap.to(helloAnimatedRef.current, {
				y: -100,
				delay: 1.5,
			})
		}
		if (weAnimatedRef && weAnimatedRef.current) {
			gsap.from(weAnimatedRef.current, {
				y: 100,
				delay: 1.6,
			})
			gsap.to(weAnimatedRef.current, {
				y: -100,
				delay: 3,
			})
		}
		if (careAnimatedRef && careAnimatedRef.current) {
			gsap.from(careAnimatedRef.current, {
				y: 100,
				delay: 3.1,
			})
			gsap.to(careAnimatedRef.current, {
				y: -100,
				delay: 6.5,
			})
		}
		if (chosenAnimatedRef && chosenAnimatedRef.current) {
			gsap.from(chosenAnimatedRef.current, {
				y: 100,
				delay: 7,
			})
			gsap.to(chosenAnimatedRef.current, {
				y: -100,
				delay: 12,
			})
		}
		if (genderAnimatedRef && genderAnimatedRef.current) {
			gsap.from(genderAnimatedRef.current, {
				y: 100,
				delay: 7.8,
			})
			gsap.to(genderAnimatedRef.current, {
				y: -100,
				delay: 12,
			})
		}
	}, [])

	return (
		<Wrapper>
			<Background />
			<LandingWrapper style={{ position: "relative" }}>
				{/* {timeline.map((step) => ( */}
				<AnimatedTextWrapper>
					<AnimatedText ref={helloAnimatedRef}>
						<Trans>Hello!</Trans>
					</AnimatedText>
				</AnimatedTextWrapper>
				<AnimatedTextWrapper>
					<AnimatedText ref={weAnimatedRef}>
						<Trans>We are Pora!</Trans>
					</AnimatedText>
				</AnimatedTextWrapper>
				<AnimatedTextWrapper>
					<AnimatedText ref={careAnimatedRef}>
						<Trans>Take care of your skin, hair, body and mind</Trans>
					</AnimatedText>
				</AnimatedTextWrapper>
				<AnimatedTextWrapper>
					<AnimatedText ref={chosenAnimatedRef}>
						<Trans>Chosen by</Trans>
					</AnimatedText>
				</AnimatedTextWrapper>
				<AnimatedTextWrapper isBottom>
					<AnimatedText ref={genderAnimatedRef}>
						{!props.onboardingType ? <Trans>Women</Trans> : <Trans>Men</Trans>}
					</AnimatedText>
				</AnimatedTextWrapper>
				{/* // ))} */}
				<Lottie
					options={linesOptions}
					isPaused={false}
					isStopped={false}
					style={lottieStyle}
					isClickToPauseDisabled={true}
					eventListeners={[
						{
							eventName: "complete",
							callback: () => {
								setIsStoppedCircleAnimation(false)
							},
						},
					]}
					width="100%"
					height="100%"
				/>
				<Lottie
					options={logoOptions}
					isStopped={false}
					isPaused={false}
					style={lottieStyle}
					isClickToPauseDisabled={true}
				/>
				<Lottie
					options={circleOptions}
					isStopped={isStoppedCircleAnimation}
					isPaused={false}
					style={lottieStyle}
					isClickToPauseDisabled={true}
					eventListeners={[
						{
							eventName: "complete",
							callback: () => props.onNext(),
						},
					]}
				/>
			</LandingWrapper>
		</Wrapper>
	)
}
